import { Component, OnInit, ViewChild, ElementRef, OnDestroy, AfterViewInit, HostListener } from '@angular/core';
import * as moment from 'moment';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { APIService } from 'src/app/services/api.service';
import { NotifierService } from 'src/app/services/notifier.service';
import { getENV, getRaceType } from 'src/app/_helpers/helpers';
import { locationMappers } from '../../locations';

@Component({
  selector: 'visualization-viewer',
  templateUrl: './visualization-viewer.component.html',
  styleUrls: ['./visualization-viewer.component.css']
})
export class VisualizationViewerComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('videoPlayer') videoPlayer: ElementRef<HTMLVideoElement>;
  @ViewChild('progressBarContainer') progressBarContainer: ElementRef<HTMLDivElement>;

  loading: boolean = false;
  videoLoading: boolean = false;
  apiError: string | null = null;
  videoError: string | null = null;
  iframeUrl: SafeResourceUrl | null = null;
  iframeHtml: SafeResourceUrl | null = null;
  videoUrl: SafeUrl | null = null;
  thumbnailUrl: SafeUrl | null = null;
  videoPlaybackRate: number = 1;
  isPlaying: boolean = false;
  isFullscreen: boolean = false;
  currentTime: number = 0;
  duration: number = 0;
  videoProgress: number = 0;
  bufferProgress: number = 0;
  layoutMode: 'vertical' | 'horizontal' = 'vertical';

  setLayout(mode: 'vertical' | 'horizontal'): void {
    this.layoutMode = mode;
  }
  // Progress bar dragging properties
  isDragging: boolean = false;
  dragListeners: Array<() => void> = [];
  hoverPosition: number = 0;
  hoverTime: number = 0;

  data: any = {};

  config: any;

  constructor(
    private apiService: APIService,
    private route: ActivatedRoute,
    private router: Router,
    private notifier: NotifierService,
    private sanitizer: DomSanitizer
  ) {
    this.config = this.route.snapshot.data['config'];
  }

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;

    const requiredParams = [
      'venue_name',
      'race_number',
      'race_id',
      'race_date',
      'external_event_id',
      'jurisdiction_code',
      'external_venue_id'
    ];

    const missing = requiredParams.filter(p => !params[p]);
    if (missing.length > 0) {
      console.error('Missing required query params:', missing);
      this.apiError = 'Missing required parameters';
      return;
    }

    this.data = {
      event: {
        venue_name: params['venue_name'],
        date: params['race_date'],
        external_event_id: params['external_event_id'],
        JurisdictionCode: params['jurisdiction_code'],
        pc: params['pc_number'] || null,
        external_venue_id: params['external_venue_id'],
      },
      race: {
        SquentialRaceOrderNumber: params['race_number'],
        ExternalRaceId: params['race_id'],
        race_length: params['race_length'],
      },
      entrants: []
    };

    this.fetchVisualizationUrl();
    this.fetchVideoReplay();
    this.fetchHorseDetails();
  }
  ngOnDestroy(): void {
    // Pause and unload video if component is destroyed
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.videoPlayer.nativeElement.pause();
      this.videoPlayer.nativeElement.src = '';
      this.videoPlayer.nativeElement.load();
    }

    // Clean up any event listeners
    this.cleanupDragListeners();
  }

  async fetchHorseDetails() {
    try {
      const apiURL = `${this.config[getENV()].raceAPI}/race-entrant-details?ExternalRaceId=${this.data.race.ExternalRaceId}&ExternalEventId=${this.data.event.external_event_id}`;
      const result = await this.apiService.getDataPromis(apiURL, {}, {});

      if (result && result.entrants) {
        this.data.entrants = result.entrants.sort((a: any, b: any) => a.StartNumber - b.StartNumber);
      }
    } catch (error) {
      console.error('Error fetching horse details:', error);
    }
  }

  async fetchVisualizationUrl() {
    this.loading = true;
    this.apiError = null;

    const payload = {
      action: 'get_visualization_presigned_url',
      location:
        locationMappers[this.data.event.venue_name] || this.data.event.venue_name,
      race_number: this.data.race.SquentialRaceOrderNumber,
      race_id: this.data.race.ExternalRaceId,
      race_date: this.data.event.date,
      type: getRaceType(this.data.event.external_venue_id)['raceTypeStr'],
      event_id: this.data.event.external_event_id,
      jurisdiction: this.data.event.JurisdictionCode,
      pc_number: this.data.event.pc,
      venue_id: this.data.event.external_venue_id,
    };

    try {
      const apiURL = `${this.config[getENV()].raceAPI}/flask-operations`;
      const result = await this.apiService.postDataPromis(apiURL, payload, {});

      if (result.status === '1' && result.presign_url) {
        // Fetch the HTML content as text
        const response = await fetch(result.presign_url);
        const htmlText = await response.text();
        this.iframeHtml = this.sanitizer.bypassSecurityTrustHtml(htmlText);
      } else {
        this.apiError = result.error_message || 'Failed to load visualization';
      }
    } catch (error) {
      this.apiError = 'Error fetching visualization';
    } finally {
      this.loading = false;
    }
  }

  async fetchVideoReplay() {
    // Only fetch video replays for New Zealand races (JurisdictionCode: 'ARC')
    // if (this.data.event.JurisdictionCode !== 'ARC') {
    //   this.videoLoading = false;
    //   // For non-NZ races, we don't show any error, just keep videoUrl null
    //   // This will trigger the "Video Coming Soon" placeholder
    //   return;
    // }

    this.videoLoading = true;
    this.videoError = null;

    const payload = {
      action: 'fetch_racing_replays',
      race_date: this.data.event.date,
      race_number: this.data.race.SquentialRaceOrderNumber,
      location: this.data.event.venue_name,
      venue_id: this.data.event.external_venue_id,
    };

    try {
      const apiURL = `${this.config[getENV()].raceAPI}/flask-operations`;
      let result = await this.apiService.postDataPromis(apiURL, payload, {});

      if (result.status === '1' && result.video_url) {
        this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(result.video_url);
        if (result.thumbnail_url) {
          this.thumbnailUrl = this.sanitizer.bypassSecurityTrustResourceUrl(result.thumbnail_url);
        }
      } else {
        this.videoError = result.error_message || 'Video replay not available';
      }
    } catch (error) {
      this.videoError = 'Error fetching video replay';
      console.error('Error fetching video replay:', error);
    } finally {
      this.videoLoading = false;
    }
  }

  refresh() {
    this.fetchVisualizationUrl();
    this.fetchVideoReplay();
  }

  backToReports() {
    const params = this.route.snapshot.queryParams;
    let formattedDate = params['race_date'];
    if (formattedDate) {
      formattedDate = moment(formattedDate, [
        'YYYY-MM-DD',
        'DD-MM-YYYY',
        'D MMMM YYYY',
        'YYYY/MM/DD',
        'MM/DD/YYYY',
        moment.ISO_8601
      ]).format('D MMMM YYYY');
    }
    this.router.navigate(['/dash/post-race/race-reports'], {
      queryParams: {
        event_id: params['external_event_id'],
        date: formattedDate
      }
    });
  }

  // Video Player Controls
  playVideo() {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.videoPlayer.nativeElement.play();
      this.isPlaying = true;
    }
  }

  pauseVideo() {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.videoPlayer.nativeElement.pause();
      this.isPlaying = false;
    }
  }

  togglePlayPause() {
    if (this.isPlaying) {
      this.pauseVideo();
    } else {
      this.playVideo();
    }
  }

  onVideoTimeUpdate() {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      this.currentTime = video.currentTime;
      this.duration = video.duration || 0;
      this.videoProgress = (this.currentTime / this.duration) * 100;
      this.bufferProgress = ((video.buffered.length > 0 ? video.buffered.end(0) : 0) / this.duration) * 100;
    }
  }
  // Handle click on the progress bar
  onProgressBarClick(event: MouseEvent) {
    if (!this.progressBarContainer) return;

    const rect = this.progressBarContainer.nativeElement.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const percentage = (offsetX / rect.width) * 100;
    this.setVideoTimeFromPercentage(percentage);
  }

  // Start drag operation
  startDrag(event: MouseEvent) {
    event.preventDefault();
    this.isDragging = true;

    // Add event listeners for mouse move and mouse up
    const mouseMoveHandler = (e: MouseEvent) => this.onDrag(e);
    const mouseUpHandler = () => this.stopDrag();

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);

    // Store these for cleanup
    this.dragListeners = [
      () => document.removeEventListener('mousemove', mouseMoveHandler),
      () => document.removeEventListener('mouseup', mouseUpHandler)
    ];
  }

  // Handle drag operation
  onDrag(event: MouseEvent) {
    if (!this.isDragging || !this.progressBarContainer) return;

    const rect = this.progressBarContainer.nativeElement.getBoundingClientRect();
    let offsetX = event.clientX - rect.left;

    // Ensure we stay within bounds
    offsetX = Math.max(0, Math.min(offsetX, rect.width));

    const percentage = (offsetX / rect.width) * 100;
    this.setVideoTimeFromPercentage(percentage);
  }

  // Stop drag operation
  stopDrag() {
    this.isDragging = false;
    this.cleanupDragListeners();
  }

  // Clean up event listeners
  cleanupDragListeners() {
    this.dragListeners.forEach(removeListener => removeListener());
    this.dragListeners = [];
  }

  // Set video time based on percentage
  setVideoTimeFromPercentage(percentage: number) {
    if (!this.videoPlayer || !this.videoPlayer.nativeElement) return;

    const video = this.videoPlayer.nativeElement;
    const newTime = (percentage / 100) * video.duration;
    video.currentTime = newTime;
    this.videoProgress = percentage;
  }

  setPlaybackRate(rate: number) {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.videoPlayer.nativeElement.playbackRate = rate;
      this.videoPlaybackRate = rate;
    }
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }

  // Jump forward by specified seconds
  jumpForward(seconds: number) {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      const newTime = Math.min(video.currentTime + seconds, video.duration);
      video.currentTime = newTime;
    }
  }

  // Jump backward by specified seconds
  jumpBackward(seconds: number) {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      const newTime = Math.max(video.currentTime - seconds, 0);
      video.currentTime = newTime;
    }
  }

  // Format time with milliseconds (mm:ss:ms)
  formatTimeWithMs(seconds: number): string {
    if (isNaN(seconds) || seconds === Infinity) return '0:00:000';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 1000);

    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}:${milliseconds.toString().padStart(3, '0')}`;
  }

  // Toggle fullscreen mode
  toggleFullscreen() {
    if (!this.videoPlayer) return;

    const videoElement = this.videoPlayer.nativeElement;
    const container = videoElement.closest('.video-container') as HTMLElement;

    if (!document.fullscreenElement) {
      if (container.requestFullscreen) {
        container.requestFullscreen()
          .then(() => this.isFullscreen = true)
          .catch(err => console.error(`Error attempting to enable fullscreen: ${err.message}`));
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
          .then(() => this.isFullscreen = false)
          .catch(err => console.error(`Error attempting to exit fullscreen: ${err.message}`));
      }
    }
  }

  onVideoLoaded() {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.duration = this.videoPlayer.nativeElement.duration;
    }
  }

  onVideoEnded() {
    this.isPlaying = false;
  }

  onVideoError() {
    this.videoError = "Failed to load video. The video might be unavailable or in an unsupported format.";
  }

  // Handle mouse hover on progress bar
  ngAfterViewInit() {
    if (this.progressBarContainer) {
      this.progressBarContainer.nativeElement.addEventListener('mousemove', (event: MouseEvent) => {
        this.updateHoverTimeDisplay(event);
      });

      this.progressBarContainer.nativeElement.addEventListener('mouseenter', () => {
        const hoverTimeDisplay = this.progressBarContainer.nativeElement.querySelector('.progress-hover-time');
        if (hoverTimeDisplay) {
          hoverTimeDisplay.classList.add('visible');
        }
      });

      this.progressBarContainer.nativeElement.addEventListener('mouseleave', () => {
        const hoverTimeDisplay = this.progressBarContainer.nativeElement.querySelector('.progress-hover-time');
        if (hoverTimeDisplay) {
          hoverTimeDisplay.classList.remove('visible');
        }
      });
    }
  }

  // Update hover time display
  updateHoverTimeDisplay(event: MouseEvent) {
    if (!this.progressBarContainer || !this.videoPlayer || !this.videoPlayer.nativeElement) return;

    const rect = this.progressBarContainer.nativeElement.getBoundingClientRect();
    let offsetX = event.clientX - rect.left;

    // Ensure we stay within bounds
    offsetX = Math.max(0, Math.min(offsetX, rect.width));

    // Calculate hover position and corresponding time
    this.hoverPosition = offsetX;
    const percentage = (offsetX / rect.width) * 100;
    this.hoverTime = (percentage / 100) * this.duration;
  }

  // Format time in mm:ss format without milliseconds
  formatTimeSimple(seconds: number): string {
    if (isNaN(seconds) || seconds === Infinity) return '0:00';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    if (this.isFullscreen) {
      this.toggleFullscreen();
    }
  }
}
